import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  HStack,
  Icon,
  Tag,
  TagLabel,
  Text,
} from "@chakra-ui/react";
import { MdAdd, MdRemove } from "react-icons/md";
import { AppIcons } from "constants/icons";
import { KGDataFrameProto } from "build-ts/dataframe";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterName } from "utils/nameUtils";

export interface KGDataFrameFiltersProps {
  /** The KG dataframe whose filters to display. Uses `kg_observation.filters` for static pills. */
  data: KGDataFrameProto;
  /** Expression filter toggle state. Pass empty object `{}` for a read-only view. */
  checkedExpressionFilters: Record<string, boolean>;
  isRecomputing: boolean;
  hasExpressionFiltersChanged: boolean;
  onExpressionFilterToggle: (filter: string) => void;
  onRecompute: () => void;
  /**
   * When true, expression filter toggle buttons and the Recompute button are hidden.
   * Use in read-only contexts (e.g. Inputs tab) where recomputation is not available.
   */
  readOnly?: boolean;
}

/**
 * Collapsible filter panel for a KGDataFrame.
 *   - Header: filter icon + "FILTERS (n)" + chevron toggle
 *   - Body: static query filter pills + togglable expression filter pills + Recompute button
 *
 * Pass `readOnly` to suppress toggle/recompute controls (e.g. in the Inputs tab).
 */
const KGDataFrameFilters: React.FC<KGDataFrameFiltersProps> = ({
  data,
  checkedExpressionFilters,
  isRecomputing,
  hasExpressionFiltersChanged,
  onExpressionFilterToggle,
  onRecompute,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const queryFilters = useMemo(
    () => data?.kg_observation?.filters || [],
    [data],
  );

  const expressionFilters = data.expression_filters ?? [];
  const totalCount = queryFilters.length + expressionFilters.length;

  if (totalCount === 0) {
    return null;
  }

  return (
    <Box borderBottom="1px solid" borderColor="gray.200">
      {/* ── Collapsible header ── */}
      <HStack
        px={3}
        py={2}
        cursor="pointer"
        onClick={() => setIsExpanded((v) => !v)}
        justifyContent="space-between"
        _hover={{ bg: "gray.100" }}
        transition="background 0.15s"
      >
        <HStack spacing={1.5}>
          <Icon as={AppIcons.filter} boxSize={3} color="purple.600" />
          <Text fontSize="xs" fontWeight="700" color="gray.700" m={0}>FILTERS</Text>
          <Text fontSize="xs" color="gray.500" m={0}>({totalCount})</Text>
        </HStack>
        <Icon
          as={isExpanded ? AppIcons.chevronUp : AppIcons.chevronDown}
          boxSize={2.5}
          color="gray.500"
        />
      </HStack>

      {/* ── Filter pills ── */}
      <Collapse in={isExpanded} animateOpacity>
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          px={2}
          py={1}
          bg="white"
        >
          {queryFilters.map((filter, i) => (
            <Tag
              key={i}
              size="sm"
              borderRadius="md"
              variant="solid"
              bg="white"
              color="gray.800"
              borderWidth="1px"
              borderColor="gray.400"
              p={1}
            >
              <TagLabel fontSize="xs" whiteSpace="nowrap" fontFamily="mono">
                <Box as="span" fontWeight="500" color="gray.900">
                  {filter?.kg_column_header?.name}
                </Box>
                <Box as="span" fontWeight="600" color="gray.800">
                  {" "}{getOperatorSymbol(filter?.operator!)}{" "}
                  {filter?.values?.length > 0
                    ? filter.values.map((v: any) => v.toString().trim()).join(", ")
                    : filter?.date_filter?.absolute_date_filter
                      ? getDateFilterName(filter.date_filter.absolute_date_filter)
                      : filter?.date_filter
                        ? getDateFilterName(filter.date_filter)
                        : ""}
                </Box>
              </TagLabel>
            </Tag>
          ))}

          {expressionFilters.map((filter, i) => {
            const isActive = checkedExpressionFilters[filter] !== false;
            return (
              <Tag
                key={`expr-${i}`}
                size="xs"
                borderRadius="md"
                variant="solid"
                bg="white"
                color={isActive ? "gray.800" : "gray.400"}
                borderWidth="1px"
                borderColor={isActive ? "gray.300" : "gray.200"}
                p={1}
                opacity={isActive ? 1 : 0.6}
              >
                <HStack spacing={1}>
                  <Icon as={AppIcons.mathFunction} boxSize={3.5} color={isActive ? "purple.500" : "gray.400"} flexShrink={0} />
                  <TagLabel
                    fontSize="2xs"
                    whiteSpace="nowrap"
                    fontFamily="mono"
                    fontWeight="600"
                    textDecoration={isActive ? "none" : "line-through"}
                  >
                    {filter}
                  </TagLabel>
                  {!readOnly && (
                    <Box
                      as="button"
                      type="button"
                      onClick={() => onExpressionFilterToggle(filter)}
                      ml={0.5}
                      color={isActive ? "red.400" : "gray.400"}
                      _hover={{ color: isActive ? "red.600" : "green.500" }}
                      fontSize="2xs"
                      lineHeight={1}
                      aria-label={isActive ? "Remove filter" : "Restore filter"}
                    >
                      {isActive ? <Icon as={MdRemove} boxSize={3} /> : <Icon as={MdAdd} boxSize={3} />}
                    </Box>
                  )}
                </HStack>
              </Tag>
            );
          })}

          {!readOnly && hasExpressionFiltersChanged && (
            <Button
              size="xs"
              colorScheme="purple"
              variant="solid"
              onClick={onRecompute}
              isLoading={isRecomputing}
              loadingText="Recomputing..."
              flexShrink={0}
            >
              Recompute
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default KGDataFrameFilters;
